import React, { useEffect, useMemo, useState, FormEvent } from "react";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import styles from "./CadastroProfissionaisForm.module.css";
import styles_button from "../../../styles/Button.module.css";
import { useCadastro } from "../../../resources/CadastroProvider";
import MultiSelectGroupField from "../../atoms/MultiSelectField/MultiSelectGroupField";
import SearchSelect, { OptionItem } from "../../atoms/SearchSelect/SearchSelect";
import getCidades from "../../../API/getCidades";
import { Option, ProfessionalData } from "../../../types/Models";
import { useDadoComplementarStore } from "../../../store/useDadoComplementarStore";
import { useDadosComplementares } from "../../../hook/useDadosComplementares";
import { findRelacionamentosCampos, RelacionamentoCampo } from "../../../API/apiDadosComplementares";
import { formatarDatetimeCompletoBr, formatarDatetimeToDataBr } from "../../../utils/Masks";

import styles_input from "../../../styles/Input.module.css";

interface Props {
  edit?: boolean;
  isOwnProfileEdit?: boolean;
  canEditOwnProfessionalData?: boolean;
}

type ProfessionalIdField =
  | "ambito_id"
  | "municipio_id"
  | "lotacao_id"
  | "vinculo_id"
  | "funcao_id"
  | "superintendencia_id"
  | "unidade_id"
  | "diretoria_regional_id"
  | "diretoria_interna_id"
  | "instituicao_id"
  | "orgao_atuacao_id";

const AMBITO_ESTADUAL_ID = 1;
const AMBITO_MUNICIPAL_ID = 2;

const LOTACAO_CEAS_ID = 1;
const LOTACAO_CREAS_REGIONAL_ID = 2;
const LOTACAO_DIRETORIA_REGIONAL_ID = 3;
const LOTACAO_SUBSECRETARIA_ID = 4;
const LOTACAO_OUTROS_ORGAOS_ID = 5;

const PROFESSIONAL_DISPLAY_FIELDS: Partial<Record<ProfessionalIdField, keyof ProfessionalData>> = {
  ambito_id: "ambito",
  municipio_id: "municipio",
  lotacao_id: "lotacao",
  vinculo_id: "vinculo_empregaticio",
  funcao_id: "funcao_desempenhada",
  superintendencia_id: "superintendencia_assessoria",
  unidade_id: "unidade",
  diretoria_regional_id: "diretoria_reginal",
  diretoria_interna_id: "diretoria_interna",
  instituicao_id: "instituicao",
  orgao_atuacao_id: "orgao_atuacao",
};

const toValue = (value?: number | ""): number | null =>
  value === "" || value === undefined ? null : value;

const getRelationshipDependentFields = (rules: RelacionamentoCampo[], sourceField: string) => {
  const dependentFields = new Set<string>();
  const pendingFields = [sourceField];

  while (pendingFields.length) {
    const currentField = pendingFields.shift();
    if (!currentField) continue;

    rules
      .filter((rule) => rule.source_field === currentField)
      .forEach((rule) => {
        if (rule.target_field === sourceField || dependentFields.has(rule.target_field)) return;
        dependentFields.add(rule.target_field);
        pendingFields.push(rule.target_field);
      });
  }

  return dependentFields;
};

const CadastroProfissionaisForm = ({
  edit,
  isOwnProfileEdit = false,
  canEditOwnProfessionalData = true,
}: Props) => {
  //const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED !== "false";
  const recaptchaEnabled = false;
  const [cidades, setCidades] = useState<OptionItem[]>([]);
  const [mensagem, setMensagem] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [qtdAreaAtuacao, setQtdAreaAtuacao] = useState<number>(4);
  const [mostraAreaAtuacao, setMostraAreaAtuacao] = useState<boolean>(false);
  const [relationshipRules, setRelationshipRules] = useState<RelacionamentoCampo[]>([]);
  const [relationshipRulesLoaded, setRelationshipRulesLoaded] = useState(false);

  const {
    loadingRegister,
    professionalData,
    setProfessionalData,
    handlePrevStep,
    handleNextStep,
    areasActivity,
    setAreasActivity,
    handleUpdateUser,
    loadingUpdate,
    captchaToken,
    setCaptchaToken,
  } = useCadastro();

  const {
    loadindDadosCompl,
    lotacoes,
    vinculos,
    funcoes,
    superintendencias,
    unidades,
    diretorias,
    diretoria_regionais,
    diretoria_internas,
    instituicoesEstaduais,
    orgaosAtuacao,
    areasAtuacao,
  } = useDadoComplementarStore();

  useDadosComplementares();

  useEffect(() => {
    let active = true;

    void findRelacionamentosCampos("professional_form")
      .then((rules) => {
        if (active) setRelationshipRules(rules);
      })
      .catch(() => {
        if (active) setRelationshipRules([]);
      })
      .finally(() => {
        if (active) setRelationshipRulesLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  const orgaoOutrasSecretariasId = useMemo(
    () =>
      orgaosAtuacao.find(
        (item) => item.nome === "Outras Secretarias/Órgãos da Prefeitura",
      )?.id ?? null,
    [orgaosAtuacao],
  );

  const orgaosComAreaIds = useMemo(() => {
    const nomesComArea = new Set([
      "Secretaria Municipal de Assistência Social",
      "Conselho Municipal de Assistência Social",
      "Entidades e Organizações de Assistência Social",
    ]);

    return orgaosAtuacao
      .filter((item) => nomesComArea.has(item.nome))
      .map((item) => item.id);
  }, [orgaosAtuacao]);

  const gabineteSuperintendenciaId = useMemo(
    () => superintendencias.find((item) => item.nome === "Gabinete")?.id ?? null,
    [superintendencias],
  );

  const ambitoOptions = useMemo<OptionItem[]>(
    () => [
      { id: AMBITO_ESTADUAL_ID, nome: "Estadual" },
      { id: AMBITO_MUNICIPAL_ID, nome: "Municipal" },
    ],
    [],
  );

  const areaOptions = useMemo<Option[]>(
    () =>
      areasAtuacao.map((item) => ({
        group: item.grupo_nome,
        name: item.nome,
      })),
    [areasAtuacao],
  );

  const selectedAreas = useMemo<Option[]>(() => {
    return areasActivity
      .map((areaId) => {
        const area = areasAtuacao.find((a) => a.id === areaId);
        if (!area) {
          return null;
        }

        return { group: area.grupo_nome, name: area.nome };
      })
      .filter((item): item is Option => !!item);
  }, [areasActivity, areasAtuacao]);

  const ambitoId = toValue(professionalData.ambito_id);
  const municipioId = toValue(professionalData.municipio_id);
  const lotacaoId = toValue(professionalData.lotacao_id);
  const superintendenciaId = toValue(professionalData.superintendencia_id);
  const orgaoAtuacaoId = toValue(professionalData.orgao_atuacao_id);
  const funcaoId = toValue(professionalData.funcao_id);
  const unidadeId = toValue(professionalData.unidade_id);
  const diretoriaRegionalId = toValue(professionalData.diretoria_regional_id);
  const diretoriaInternaId = toValue(professionalData.diretoria_interna_id);
  const instituicaoId = toValue(professionalData.instituicao_id);
  const professionalFormLocked = edit && isOwnProfileEdit && !canEditOwnProfessionalData;
  const lastProfessionalUpdate = useMemo(() => {
    if (!professionalData.updated_at) {
      return "Nunca atualizado";
    }

    const formatted = formatarDatetimeCompletoBr(professionalData.updated_at);
    const date = new Date(professionalData.updated_at);

    if (!formatted) {
      return "Data inválida";
    }

    return `${formatarDatetimeToDataBr(professionalData.updated_at)} às ${date.toLocaleTimeString(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      },
    )}`;
  }, [professionalData.updated_at]);

  const diretoriasRegionaisOptions = useMemo<OptionItem[]>(
    () =>
      diretoria_regionais.filter(
        (item) => item.tipo?.toUpperCase() === "REGIONAL",
      ),
    [diretoria_regionais],
  );

  const diretoriasVinculadasOptions = useMemo<OptionItem[]>(
    () =>
      diretoria_internas.filter((item) => {
        const tipo = item.tipo?.toUpperCase();
        const isDiretoriaVinculada = tipo === "VINCULADA" || tipo === "INTERNA";

        return isDiretoriaVinculada && item.superintendencia_id === superintendenciaId;
      }),
    [diretoria_internas, superintendenciaId],
  );

  const updateAreaVisibilityByLotacao = (value: number | null) => {
    setQtdAreaAtuacao(4);
    setMostraAreaAtuacao(false);

    switch (value) {
      case LOTACAO_CEAS_ID:
        setMostraAreaAtuacao(true);
        setQtdAreaAtuacao(4);
        break;
      case LOTACAO_CREAS_REGIONAL_ID:
      case LOTACAO_DIRETORIA_REGIONAL_ID:
      case LOTACAO_SUBSECRETARIA_ID:
      case LOTACAO_OUTROS_ORGAOS_ID:
        setMostraAreaAtuacao(false);
        setQtdAreaAtuacao(4);
        break;
      default:
        setQtdAreaAtuacao(4);
        setMostraAreaAtuacao(false);
        break;
    }
  };

  const updateAreaVisibilityByOrgao = (value: number | null) => {
    setQtdAreaAtuacao(4);
    setMostraAreaAtuacao(false);

    if (value && orgaosComAreaIds.includes(value)) {
      setMostraAreaAtuacao(true);
      setQtdAreaAtuacao(4);
      return;
    }

    if (value && value === orgaoOutrasSecretariasId) {
      setQtdAreaAtuacao(4);
      setMostraAreaAtuacao(false);
      return;
    }

    setQtdAreaAtuacao(4);
    setMostraAreaAtuacao(false);
  };

  const handleIdChange = (field: ProfessionalIdField, value: number | null) => {
    const normalizedValue = value ?? "";
    const useRelationshipRules = relationshipRulesLoaded && relationshipRules.length > 0;
    const dependentFields = useRelationshipRules
      ? getRelationshipDependentFields(relationshipRules, field)
      : new Set<string>();

    if (field === "ambito_id" || field === "lotacao_id") {
      setAreasActivity([]);
    }

    if (dependentFields.has("areas_atuacao")) {
      setAreasActivity([]);
    }

    if (field === "ambito_id") {
      setQtdAreaAtuacao(4);
      setMostraAreaAtuacao(false);
    }

    if (field === "lotacao_id") {
      updateAreaVisibilityByLotacao(value);
    }

    if (field === "orgao_atuacao_id") {
      updateAreaVisibilityByOrgao(value);
    }

    if (field === "superintendencia_id") {
      setMostraAreaAtuacao(false);
      if (value && value === gabineteSuperintendenciaId) {
        setMostraAreaAtuacao(true);
      }
    }

    if (
      field === "unidade_id" ||
      field === "diretoria_regional_id" ||
      field === "diretoria_interna_id" ||
      field === "instituicao_id"
    ) {
      setMostraAreaAtuacao(false);
      if (value) {
        setMostraAreaAtuacao(true);
      }
    }

    setProfessionalData((prevData) => {
      const newData: ProfessionalData = { ...prevData, [field]: normalizedValue };

      dependentFields.forEach((dependentField) => {
        if (dependentField === "areas_atuacao" || dependentField === field) return;
        const professionalField = dependentField as ProfessionalIdField;
        newData[professionalField] = "";
        const displayField = PROFESSIONAL_DISPLAY_FIELDS[professionalField];
        if (displayField) newData[displayField] = "";
      });

      if (field === "ambito_id") {
        newData.ambito = value === AMBITO_ESTADUAL_ID ? "Estadual" : value === AMBITO_MUNICIPAL_ID ? "Municipal" : "";
        newData.lotacao_id = "";
        newData.lotacao = "";
        newData.unidade_id = "";
        newData.unidade = "";
        newData.diretoria_regional_id = "";
        newData.diretoria_reginal = "";
        newData.superintendencia_id = "";
        newData.superintendencia_assessoria = "";
        newData.diretoria_interna_id = "";
        newData.diretoria_interna = "";
        newData.instituicao_id = "";
        newData.instituicao = "";
        newData.orgao_atuacao_id = "";
        newData.orgao_atuacao = "";
      }

      if (field === "municipio_id") {
        newData.municipio = cidades.find((item) => item.id === value)?.nome || "";
      }

      if (field === "lotacao_id") {
        newData.lotacao = lotacoes.find((item) => item.id === value)?.nome || "";
        newData.unidade_id = "";
        newData.unidade = "";
        newData.superintendencia_id = "";
        newData.superintendencia_assessoria = "";
        newData.diretoria_regional_id = "";
        newData.diretoria_reginal = "";
        newData.diretoria_interna_id = "";
        newData.diretoria_interna = "";
        newData.instituicao_id = "";
        newData.instituicao = "";
        newData.orgao_atuacao_id = "";
        newData.orgao_atuacao = "";
      }

      if (field === "vinculo_id") {
        newData.vinculo_empregaticio = vinculos.find((item) => item.id === value)?.nome || "";
      }

      if (field === "funcao_id") {
        newData.funcao_desempenhada = funcoes.find((item) => item.id === value)?.nome || "";
      }

      if (field === "superintendencia_id") {
        newData.superintendencia_assessoria =
          superintendencias.find((item) => item.id === value)?.nome || "";
        newData.diretoria_interna_id = "";
        newData.diretoria_interna = "";
      }

      if (field === "unidade_id") {
        newData.unidade = unidades.find((item) => item.id === value)?.nome || "";
      }

      if (field === "diretoria_regional_id") {
        newData.diretoria_reginal =
          diretoriasRegionaisOptions.find((item) => item.id === value)?.nome || "";
      }

      if (field === "diretoria_interna_id") {
        newData.diretoria_interna =
          diretoriasVinculadasOptions.find((item) => item.id === value)?.nome || "";
      }

      if (field === "instituicao_id") {
        newData.instituicao =
          instituicoesEstaduais.find((item) => item.id === value)?.nome || "";
      }

      if (field === "orgao_atuacao_id") {
        newData.orgao_atuacao = orgaosAtuacao.find((item) => item.id === value)?.nome || "";
      }

      return newData;
    });
  };

  const validateFields = () => {
    const validationErrors: Record<string, string> = {};

    if (!ambitoId) {
      validationErrors.ambito_id = "O campo 'Âmbito de Atuação' é obrigatório.";
    }

    if (!useRelationshipRules && !municipioId) {
      validationErrors.municipio_id = "O campo 'Município de Atuação' é obrigatório.";
    }

    if (useRelationshipRules) {
      relationshipTargetRules.forEach((rule) => {
        const isRequired = rule.obrigatorio === true || rule.obrigatorio === 1;
        if (!isRequired) return;

        if (rule.target_field === "areas_atuacao") {
          if (!areasActivity.length) validationErrors.areasActivity = `O campo '${rule.target_label}' é obrigatório.`;
          return;
        }

        const value = getRelationshipSourceValue(rule.target_field);
        if (value === "" || value === null || value === undefined) {
          validationErrors[rule.target_field] = `O campo '${rule.target_label}' é obrigatório.`;
        }
      });

      return validationErrors;
    }

    if (ambitoId === AMBITO_ESTADUAL_ID) {
      if (!lotacaoId) {
        validationErrors.lotacao_id = "O campo 'Lotação' é obrigatório.";
      }

      if (lotacaoId === LOTACAO_CREAS_REGIONAL_ID && !toValue(professionalData.unidade_id)) {
        validationErrors.unidade_id = "O campo 'Unidade CREAS Regional' é obrigatório.";
      }

      if (lotacaoId === LOTACAO_DIRETORIA_REGIONAL_ID && !toValue(professionalData.diretoria_regional_id)) {
        validationErrors.diretoria_regional_id = "O campo 'Diretoria Regional' é obrigatório.";
      }

      if (lotacaoId === LOTACAO_SUBSECRETARIA_ID) {
        if (!superintendenciaId) {
          validationErrors.superintendencia_id = "O campo 'Superintendência' é obrigatório.";
        }

        if (
          superintendenciaId &&
          superintendenciaId !== gabineteSuperintendenciaId &&
          !toValue(professionalData.diretoria_interna_id)
        ) {
          validationErrors.diretoria_interna_id = "O campo 'Diretoria Vinculada' é obrigatório.";
        }
      }

      if (lotacaoId === LOTACAO_OUTROS_ORGAOS_ID && !toValue(professionalData.instituicao_id)) {
        validationErrors.instituicao_id = "O campo 'Instituição' é obrigatório.";
      }
    }

    if (ambitoId === AMBITO_MUNICIPAL_ID) {
      if (!orgaoAtuacaoId) {
        validationErrors.orgao_atuacao_id = "O campo 'Órgão de Atuação' é obrigatório.";
      }

      if (orgaoAtuacaoId && orgaoAtuacaoId === orgaoOutrasSecretariasId && !toValue(professionalData.instituicao_id)) {
        validationErrors.instituicao_id = "O campo 'Instituição' é obrigatório.";
      }
    }

    if (!areasActivity || areasActivity.length === 0) {
      validationErrors.areasActivity = "O campo 'Área de Atuação' é obrigatório.";
    }

    if (!funcaoId) {
      validationErrors.funcao_id = "O campo 'Função Desempenhada' é obrigatório.";
    }

    if (!toValue(professionalData.vinculo_id)) {
      validationErrors.vinculo_id = "O campo 'Vínculo Empregatício' é obrigatório.";
    }

    return validationErrors;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    setMensagem("");
    setErrors({});

    if (professionalFormLocked) {
      setMensagem(
        "Os dados profissionais só podem ser alterados após seis meses da última atualização.",
      );
      return;
    }

    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!edit && recaptchaEnabled && !captchaToken) {
      setMensagem("Confirme que você não é um robô.");
      return;
    }

    if (edit) {
      handleUpdateUser(5);
    } else {
      handleNextStep();
    }
  };

  const handleAreasActivityChange = (value: Option[]) => {
    const mappedIds = value
      .map((item) => {
        const area = areasAtuacao.find(
          (areaItem) => areaItem.nome === item.name && areaItem.grupo_nome === item.group,
        );
        return area?.id;
      })
      .filter((item): item is number => typeof item === "number");

    const useRelationshipRules = relationshipRulesLoaded && relationshipRules.length > 0;
    const dependentFields = useRelationshipRules
      ? getRelationshipDependentFields(relationshipRules, "areas_atuacao")
      : new Set<string>();

    if (dependentFields.size) {
      setProfessionalData((previousData) => {
        const nextData: ProfessionalData = { ...previousData };
        dependentFields.forEach((dependentField) => {
          if (dependentField === "areas_atuacao") return;
          const professionalField = dependentField as ProfessionalIdField;
          nextData[professionalField] = "";
          const displayField = PROFESSIONAL_DISPLAY_FIELDS[professionalField];
          if (displayField) nextData[displayField] = "";
        });
        return nextData;
      });
    }

    setAreasActivity(mappedIds);
  };

  const fetchCidades = async () => {
    try {
      const cidadesData = await getCidades();
      setCidades(cidadesData || []);
    } catch (error) {
      console.error("Erro ao buscar cidades:", error);
    }
  };

  useEffect(() => {
    fetchCidades();
  }, []);

  useEffect(() => {
    let nextMostraAreaAtuacao = false;
    let nextQtdAreaAtuacao = 4;

    if (ambitoId === AMBITO_ESTADUAL_ID) {
      switch (lotacaoId) {
        case LOTACAO_CEAS_ID:
          nextMostraAreaAtuacao = true;
          nextQtdAreaAtuacao = 4;
          break;
        case LOTACAO_CREAS_REGIONAL_ID:
        case LOTACAO_DIRETORIA_REGIONAL_ID:
        case LOTACAO_SUBSECRETARIA_ID:
        case LOTACAO_OUTROS_ORGAOS_ID:
          nextQtdAreaAtuacao = 4;
          break;
        default:
          nextQtdAreaAtuacao = 4;
          break;
      }

      if (unidadeId || diretoriaRegionalId || diretoriaInternaId || instituicaoId) {
        nextMostraAreaAtuacao = true;
      }

      if (superintendenciaId === gabineteSuperintendenciaId) {
        nextMostraAreaAtuacao = true;
      }
    }

    if (ambitoId === AMBITO_MUNICIPAL_ID) {
      if (orgaoAtuacaoId && orgaosComAreaIds.includes(orgaoAtuacaoId)) {
        nextMostraAreaAtuacao = true;
        nextQtdAreaAtuacao = 4;
      } else if (orgaoAtuacaoId === orgaoOutrasSecretariasId) {
        nextQtdAreaAtuacao = 4;
      }

      if (instituicaoId) {
        nextMostraAreaAtuacao = true;
      }
    }

    setQtdAreaAtuacao(nextQtdAreaAtuacao);
    setMostraAreaAtuacao(nextMostraAreaAtuacao);
  }, [
    ambitoId,
    lotacaoId,
    orgaoAtuacaoId,
    unidadeId,
    diretoriaRegionalId,
    diretoriaInternaId,
    instituicaoId,
    superintendenciaId,
    gabineteSuperintendenciaId,
    orgaosComAreaIds,
    orgaoOutrasSecretariasId,
  ]);

  const getRelationshipSourceValue = (field: string): unknown => {
    if (field === "areas_atuacao") return areasActivity;
    return (professionalData as unknown as Record<string, unknown>)[field];
  };

  const activeRelationshipRules = useMemo(() => {
    return relationshipRules
      .filter((rule) => {
        const sourceValue = getRelationshipSourceValue(rule.source_field);
        const hasSourceValue = Array.isArray(sourceValue)
          ? sourceValue.length > 0
          : sourceValue !== "" && sourceValue !== null && sourceValue !== undefined;

        if (!hasSourceValue) return false;
        if (rule.aplicar_para_todos === true || rule.aplicar_para_todos === 1 || rule.source_value_id === null) {
          return true;
        }

        return Number(sourceValue) === Number(rule.source_value_id);
      })
      .sort((first, second) => (first.ordem ?? 0) - (second.ordem ?? 0));
  }, [areasActivity, professionalData, relationshipRules]);

  const relationshipTargetRules = useMemo(() => {
    const rulesByTarget = new Map<string, RelacionamentoCampo>();
    activeRelationshipRules.forEach((rule) => {
      if (!rulesByTarget.has(rule.target_field)) rulesByTarget.set(rule.target_field, rule);
    });
    return Array.from(rulesByTarget.values());
  }, [activeRelationshipRules]);

  const relationshipOptionsByTable = useMemo<Record<string, OptionItem[]>>(() => ({
    ambitos: ambitoOptions,
    municipios: cidades,
    lotacoes,
    vinculos_empregaticios: vinculos,
    funcoes_desempenhadas: funcoes,
    superintendencias,
    unidades_regionais: unidades,
    diretorias: diretorias as OptionItem[],
    orgaos_atuacao: orgaosAtuacao,
    instituicoes_estaduais: instituicoesEstaduais,
    areas_atuacao: areasAtuacao as OptionItem[],
  }), [ambitoOptions, areasAtuacao, cidades, diretorias, funcoes, instituicoesEstaduais, lotacoes, orgaosAtuacao, superintendencias, unidades, vinculos]);

  const getFilteredRelationshipOptions = (rule: RelacionamentoCampo) => {
    const options = relationshipOptionsByTable[rule.options_table] || [];

    return options.filter((option) => (rule.options_filters || []).every((filter) => {
      const expectedValue = filter.source_field
        ? getRelationshipSourceValue(filter.source_field)
        : filter.value;
      const optionValue = (option as unknown as Record<string, unknown>)[filter.field];
      const equals = String(optionValue ?? "") === String(expectedValue ?? "");
      return filter.operator === "!=" ? !equals : equals;
    }));
  };

  const renderRelationshipTargetField = (rule: RelacionamentoCampo) => {
    const baseLabel = rule.target_field === "municipio_id"
      ? "Município de Atuação"
      : rule.target_label;
    const label = `${baseLabel}${rule.obrigatorio === true || rule.obrigatorio === 1 ? " *" : ""}`;

    if (rule.target_field === "areas_atuacao") {
      const filteredAreas = getFilteredRelationshipOptions(rule);
      const groupedOptions = filteredAreas.map((area) => ({
        group: String((area as unknown as Record<string, unknown>).grupo_nome || ""),
        name: area.nome,
      }));

      return (
        <div key={rule.target_field} className={styles.inputGroupFull}>
          <label className={styles.titleInput}>{label}</label>
          <MultiSelectGroupField
            label=""
            fullWidth
            value={selectedAreas}
            onChange={handleAreasActivityChange}
            options={groupedOptions}
            disabled={professionalFormLocked}
            required={rule.obrigatorio === true || rule.obrigatorio === 1}
            maxSelections={4}
            error={!!errors.areasActivity}
            helperText={errors.areasActivity}
          />
        </div>
      );
    }

    if (![
      "municipio_id",
      "lotacao_id",
      "vinculo_id",
      "funcao_id",
      "superintendencia_id",
      "unidade_id",
      "diretoria_regional_id",
      "diretoria_interna_id",
      "instituicao_id",
      "orgao_atuacao_id",
    ].includes(rule.target_field)) {
      return null;
    }

    const field = rule.target_field as ProfessionalIdField;
    return (
      <div key={rule.target_field} className={styles.inputGroupFull}>
        <label className={styles.titleInput}>{label}</label>
        <SearchSelect
          value={toValue((professionalData as unknown as Record<string, number | "">)[field])}
          onChange={(id) => handleIdChange(field, id)}
          options={getFilteredRelationshipOptions(rule)}
          disabled={professionalFormLocked}
          paperClassname={styles_input.paper}
          popperClassname={styles_input.popper}
          labelClassname={styles_input.labelSelect}
          inputClassname={styles_input.inputSelect}
        />
        {errors[field] && <Typography color="error">{errors[field]}</Typography>}
      </div>
    );
  };

  const useRelationshipRules = relationshipRulesLoaded && relationshipRules.length > 0;

  return (
    <div className={styles.container} data-testid="dados-profissionais-form">
      <div className={styles.card}>
        <div className={styles.title}>Dados Profissionais</div>

        <Typography variant="body2" className={styles.lastUpdate}>
          Última atualização dos dados profissionais: {lastProfessionalUpdate}
        </Typography>

        <div className={styles.message}>
          {mensagem && <Typography color="error">{mensagem}</Typography>}
        </div>

        {professionalFormLocked && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            Atenção! Os dados profissionais só podem ser alterados caso a última data de alteração for maior que seis meses. Caso seja necessário realizar alguma alteração antes do prazo, por favor entre em contato através do e-mail dgtep@social.mg.gov.br.
          </Alert>
        )}

        {loadindDadosCompl ? (
          <div
            style={{
              minHeight: "220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="textSecondary">
              Carregando dados complementares...
            </Typography>
          </div>
        ) : useRelationshipRules ? (
          <div className={styles.containerInputs}>
            <div className={styles.inputGroup}>
              <label className={styles.titleInput}>Âmbito de Atuação *</label>
              <SearchSelect
                value={toValue(professionalData.ambito_id)}
                onChange={(id) => handleIdChange("ambito_id", id)}
                options={ambitoOptions}
                disabled={professionalFormLocked}
                paperClassname={styles_input.paper}
                popperClassname={styles_input.popper}
                labelClassname={styles_input.labelSelect}
                inputClassname={styles_input.inputSelect}
              />
              {errors.ambito_id && <Typography color="error">{errors.ambito_id}</Typography>}
            </div>
            {relationshipTargetRules.map(renderRelationshipTargetField)}
          </div>
        ) : (
        <div className={styles.containerInputs}>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>Âmbito de Atuação *</label>
            <SearchSelect
              value={toValue(professionalData.ambito_id)}
              onChange={(id) => handleIdChange("ambito_id", id)}
              options={ambitoOptions}
              disabled={professionalFormLocked}
              paperClassname={styles_input.paper}
              popperClassname={styles_input.popper}
              labelClassname={styles_input.labelSelect}
              inputClassname={styles_input.inputSelect}
            />
            {errors.ambito_id && <Typography color="error">{errors.ambito_id}</Typography>}
          </div>

          {ambitoId !== null && (
            <div className={styles.inputGroup}>
              <label className={styles.titleInput}>Município de Atuação *</label>
              <SearchSelect
                value={toValue(professionalData.municipio_id)}
                onChange={(id) => handleIdChange("municipio_id", id)}
                options={cidades}
                disabled={professionalFormLocked}
                paperClassname={styles_input.paper}
                popperClassname={styles_input.popper}
                labelClassname={styles_input.labelSelect}
                inputClassname={styles_input.inputSelect}
              />
              {errors.municipio_id && <Typography color="error">{errors.municipio_id}</Typography>}
            </div>
          )}

          {ambitoId === AMBITO_ESTADUAL_ID && (
            <>
              <div className={styles.inputGroupFull}>
                <label className={styles.titleInput}>Lotação *</label>
                <SearchSelect
                  value={toValue(professionalData.lotacao_id)}
                  onChange={(id) => handleIdChange("lotacao_id", id)}
                  options={lotacoes}
                  disabled={professionalFormLocked}
                  paperClassname={styles_input.paper}
                  popperClassname={styles_input.popper}
                  labelClassname={styles_input.labelSelect}
                  inputClassname={styles_input.inputSelect}
                />
                {errors.lotacao_id && <Typography color="error">{errors.lotacao_id}</Typography>}
              </div>

              {lotacaoId === LOTACAO_CREAS_REGIONAL_ID && (
                <div className={styles.inputGroupFull}>
                  <label className={styles.titleInput}>Unidade CREAS Regional *</label>
                  <SearchSelect
                    value={toValue(professionalData.unidade_id)}
                    onChange={(id) => handleIdChange("unidade_id", id)}
                    options={unidades}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.unidade_id && <Typography color="error">{errors.unidade_id}</Typography>}
                </div>
              )}

              {lotacaoId === LOTACAO_DIRETORIA_REGIONAL_ID && (
                <div className={styles.inputGroupFull}>
                  <label className={styles.titleInput}>Diretoria Regional *</label>
                  <SearchSelect
                    value={toValue(professionalData.diretoria_regional_id)}
                    onChange={(id) => handleIdChange("diretoria_regional_id", id)}
                    options={diretoriasRegionaisOptions}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.diretoria_regional_id && (
                    <Typography color="error">{errors.diretoria_regional_id}</Typography>
                  )}
                </div>
              )}

              {lotacaoId === LOTACAO_SUBSECRETARIA_ID && (
                <>
                  <div className={styles.inputGroupFull}>
                    <label className={styles.titleInput}>Superintendência *</label>
                    <SearchSelect
                      value={toValue(professionalData.superintendencia_id)}
                      onChange={(id) => handleIdChange("superintendencia_id", id)}
                      options={superintendencias}
                      disabled={professionalFormLocked}
                      paperClassname={styles_input.paper}
                      popperClassname={styles_input.popper}
                      labelClassname={styles_input.labelSelect}
                      inputClassname={styles_input.inputSelect}
                    />
                    {errors.superintendencia_id && (
                      <Typography color="error">{errors.superintendencia_id}</Typography>
                    )}
                  </div>

                  {superintendenciaId !== null &&
                    superintendenciaId !== gabineteSuperintendenciaId && (
                      <div className={styles.inputGroupFull}>
                        <label className={styles.titleInput}>Diretoria Vinculada *</label>
                        <SearchSelect
                          value={toValue(professionalData.diretoria_interna_id)}
                          onChange={(id) => handleIdChange("diretoria_interna_id", id)}
                          options={diretoriasVinculadasOptions}
                          disabled={professionalFormLocked}
                          paperClassname={styles_input.paper}
                          popperClassname={styles_input.popper}
                          labelClassname={styles_input.labelSelect}
                          inputClassname={styles_input.inputSelect}
                        />
                        {errors.diretoria_interna_id && (
                          <Typography color="error">{errors.diretoria_interna_id}</Typography>
                        )}
                      </div>
                    )}
                </>
              )}

              {lotacaoId === LOTACAO_OUTROS_ORGAOS_ID && (
                <div className={styles.inputGroupFull}>
                  <label className={styles.titleInput}>Instituição *</label>
                  <SearchSelect
                    value={toValue(professionalData.instituicao_id)}
                    onChange={(id) => handleIdChange("instituicao_id", id)}
                    options={instituicoesEstaduais}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.instituicao_id && <Typography color="error">{errors.instituicao_id}</Typography>}
                </div>
              )}

              {mostraAreaAtuacao && (
                <div className={styles.inputGroupFull}>
                  <div className={styles.areaTitle}>
                    <label className={styles.titleInput2}>Area de Atuação *</label>
                    <span className={styles.subtitleInput}>
                      (Max. {qtdAreaAtuacao === 1 ? `${qtdAreaAtuacao} opção` : `${qtdAreaAtuacao} opções`})
                    </span>
                  </div>

                  <MultiSelectGroupField
                    label=""
                    fullWidth
                    value={selectedAreas}
                    onChange={handleAreasActivityChange}
                    options={areaOptions}
                    disabled={professionalFormLocked}
                    required
                    maxSelections={4}
                    error={!!errors.areasActivity}
                    helperText={errors.areasActivity}
                  />
                </div>
              )}

              {areasActivity.length > 0 && (
                <div className={styles.inputGroup}>
                  <label className={styles.titleInput}>Função Desempenhada *</label>
                  <SearchSelect
                    value={toValue(professionalData.funcao_id)}
                    onChange={(id) => handleIdChange("funcao_id", id)}
                    options={funcoes}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.funcao_id && <Typography color="error">{errors.funcao_id}</Typography>}
                </div>
              )}

              {funcaoId !== null && (
                <div className={styles.inputGroup}>
                  <label className={styles.titleInput}>Vínculo Empregatício *</label>
                  <SearchSelect
                    value={toValue(professionalData.vinculo_id)}
                    onChange={(id) => handleIdChange("vinculo_id", id)}
                    options={vinculos}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.vinculo_id && <Typography color="error">{errors.vinculo_id}</Typography>}
                </div>
              )}
            </>
          )}

          {ambitoId === AMBITO_MUNICIPAL_ID && (
            <>
              {municipioId !== null && (
                <div className={styles.inputGroupFull}>
                  <label className={styles.titleInput}>Órgão de Atuação *</label>
                  <SearchSelect
                    value={toValue(professionalData.orgao_atuacao_id)}
                    onChange={(id) => handleIdChange("orgao_atuacao_id", id)}
                    options={orgaosAtuacao}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.orgao_atuacao_id && (
                    <Typography color="error">{errors.orgao_atuacao_id}</Typography>
                  )}
                </div>
              )}

              {orgaoAtuacaoId !== null && orgaoAtuacaoId === orgaoOutrasSecretariasId && (
                <div className={styles.inputGroupFull}>
                  <label className={styles.titleInput}>Instituição *</label>
                  <SearchSelect
                    value={toValue(professionalData.instituicao_id)}
                    onChange={(id) => handleIdChange("instituicao_id", id)}
                    options={instituicoesEstaduais}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.instituicao_id && <Typography color="error">{errors.instituicao_id}</Typography>}
                </div>
              )}

              {mostraAreaAtuacao && (
                <div className={styles.inputGroupFull}>
                  <div className={styles.areaTitle}>
                    <label className={styles.titleInput2}>Area de Atuação *</label>
                    <span className={styles.subtitleInput}>
                      (Max. {qtdAreaAtuacao === 1 ? `${qtdAreaAtuacao} opção` : `${qtdAreaAtuacao} opções`})
                    </span>
                  </div>

                  <MultiSelectGroupField
                    label=""
                    fullWidth
                    value={selectedAreas}
                    onChange={handleAreasActivityChange}
                    options={areaOptions}
                    disabled={professionalFormLocked}
                    required
                    maxSelections={4}
                    error={!!errors.areasActivity}
                    helperText={errors.areasActivity}
                  />
                </div>
              )}

              {areasActivity.length > 0 && (
                <div className={styles.inputGroup}>
                  <label className={styles.titleInput}>Função Desempenhada *</label>
                  <SearchSelect
                    value={toValue(professionalData.funcao_id)}
                    onChange={(id) => handleIdChange("funcao_id", id)}
                    options={funcoes}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.funcao_id && <Typography color="error">{errors.funcao_id}</Typography>}
                </div>
              )}

              {funcaoId !== null && (
                <div className={styles.inputGroup}>
                  <label className={styles.titleInput}>Vínculo Empregatício *</label>
                  <SearchSelect
                    value={toValue(professionalData.vinculo_id)}
                    onChange={(id) => handleIdChange("vinculo_id", id)}
                    options={vinculos}
                    disabled={professionalFormLocked}
                    paperClassname={styles_input.paper}
                    popperClassname={styles_input.popper}
                    labelClassname={styles_input.labelSelect}
                    inputClassname={styles_input.inputSelect}
                  />
                  {errors.vinculo_id && <Typography color="error">{errors.vinculo_id}</Typography>}
                </div>
              )}
            </>
          )}
        </div>
        )}

        {!loadindDadosCompl && !edit ? (
          <>
          {recaptchaEnabled && (
            <div className={styles.captchaWrapper}>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                onChange={(token: string | null) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
                onErrored={() => setCaptchaToken(null)}
              />
            </div>
          )}
          <div className={styles.buttonContainer}>
            <Button
              disabled={loadingRegister}
              variant="contained"
              sx={{
                  width: { xs: "136px", sm: "168px" },
                  height: { xs: "42px", sm: "46px" },
                paddingY: 2,
                paddingX: 6,
                backgroundColor: "#2C3D8F",
                color: "white",
                fontWeight: 500,
                textAlign: "center",
                borderRadius: "9999px",
                "&:hover": {
                  backgroundColor: "#4556A8",
                  boxShadow: "none",
                },
                transition: "background-color 200ms ease-in-out",
              }}
              color="primary"
              onClick={handlePrevStep}
            >
              Voltar
            </Button>
            <Button
              disabled={loadingRegister}
              variant="contained"
              sx={{
                  width: { xs: "136px", sm: "168px" },
                  height: { xs: "42px", sm: "46px" },
                paddingY: 2,
                paddingX: 6,
                backgroundColor: "#5A81FA",
                color: "white",
                fontWeight: 500,
                textAlign: "center",
                borderRadius: "9999px",
                "&:hover": {
                  backgroundColor: "#7A9BFB",
                  boxShadow: "none",
                },
                transition: "background-color 200ms ease-in-out",
              }}
              className={styles.nextbutton}
              onClick={handleSubmit}
            >
              {loadingRegister ? "Cadastrando..." : "Próximo"}
            </Button>
          </div>
          </>
        ) : !loadindDadosCompl ? (
          <Button
            variant="contained"
            sx={{
              width: { xs: "136px", sm: "168px" },
              height: { xs: "42px", sm: "46px" },
              paddingY: 1,
              paddingX: 6,
              backgroundColor: "#5A81FA",
              color: "white",
              fontWeight: 500,
              textAlign: "center",
              borderRadius: "9999px",
              "&:hover": {
                backgroundColor: "#7A9BFB",
                boxShadow: "none",
              },
              transition: "background-color 200ms ease-in-out",
            }}
            disabled={loadingUpdate || professionalFormLocked}
            className={`${styles_button.buttonPrimary} ${(loadingUpdate) ? styles_button.buttonLoad : ""} `}
            onClick={handleSubmit}
          >
            {loadingUpdate ? (
              <>
                <div className={styles_button.spinner}></div>
                <span>Salvando...</span>
              </>
            ) : (
              <span>Salvar</span>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default CadastroProfissionaisForm;
